import React, { createContext, useState, useContext, ReactNode, useCallback, useEffect } from "react";
import { InfluencerLocation } from "@foodfluence/shared";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { firestore } from "../firebase";
import { InfluencerDataContext } from "./InfluencerDataContext";

interface InfluencerLocationContextType {
    influencerLocations: InfluencerLocation[];
    loading: boolean;
}

export const InfluencerLocationContext = createContext<InfluencerLocationContextType>({
    influencerLocations: [],
    loading: false,
});

interface InfluencerLocationProviderProps {
    children: ReactNode;
}

export const InfluencerLocationProvider: React.FC<InfluencerLocationProviderProps> = ({ children }) => {
    const { influencerData } = useContext(InfluencerDataContext);
    const [influencerLocations, setInfluencerLocations] = useState<InfluencerLocation[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!influencerData?.id) {
            setInfluencerLocations([]);
            return;
        }

        setLoading(true);

        // Create a query for this influencer's locations
        const locationsRef = collection(firestore, "influencerLocations");
        const q = query(locationsRef, where("influencerId", "==", influencerData.id));

        // Set up real-time listener
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const locations = snapshot.docs.map(doc => ({
                ...doc.data(),
                docId: doc.id
            } as InfluencerLocation));
            locations.sort((a, b) => (a.locationPriority ?? 999) - (b.locationPriority ?? 999));
            setInfluencerLocations(locations);
            setLoading(false);
        }, (error) => {
            console.error("Error listening to location updates:", error);
            setInfluencerLocations([]);
            setLoading(false);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [influencerData?.id]);

    return (
        <InfluencerLocationContext.Provider value={{ influencerLocations, loading }}>
            {children}
        </InfluencerLocationContext.Provider>
    );
};

export const useInfluencerLocations = () => useContext(InfluencerLocationContext);
